import { NumberOption, OrchestrationDataType } from './calculateFeatures';
import { AttributesTable } from './inferenceArtifacts';

export interface PostProcessingActionInputCondition {
  key: string;
  value: string;
  action: string;
}

export interface Input {
  attributesTable?: AttributesTable;
  limitMultipleTo?: number;
  inputKey: string;
  inputLabel: string;
  inputType: string;
  inputSource?: string | string[];
  inputSourceDependentOn?: string;
  required: boolean | PostProcessingActionInputCondition;
  inputRange?: {
    min?: number;
    max?: number;
  };
  freeSolo?: boolean;
  defaultValue?: number | boolean | string;
  displayCondition?: PostProcessingActionInputCondition;
  advanced?: boolean;
  children?: Input[];
  parentInputKey?: string; // generated in run time, used to access the correct path in th eaction object (action[parentInputKey][index][inputKey])
}

export enum ActionInput {
  FEATURE_FAMILY = 'feature_family',
  CELL = 'cell',
  AREA = 'area',
}

export default interface PostProcessingAction {
  id: string;
  actionInput: string;
  actionName: string;
  actionDisplayName: string;
  inputs: Input[];
  deletedAt?: string;
  newCellColumns?: string[];
}

export interface PostProcessingActionCreated {
  id: string;
  actionId: string;
  actionInput: string;
  actionName: string;
  newOptionValues?: string[];
  newOptionValueInput?: string;
  dependentOnInputTypes?: {
    [inputDependentOn: string]: string; // a map between the input dependent on and the input dependent on type.
  };
  [string: string]: any; // All the fields that will be added by the user in the form, the key will be the inputKey
}

export interface CategoricalOption {
  id: string | number;
  label: string | number;
}

export enum MappingFilterMetadataFrom {
  ClassConfig = 'ClassConfig',
  Metadata = 'Metadata',
}

export interface MappingFilterMetadata {
  sourceName: string;
  sourceDisplayName?: string;
  data: {
    label: string;
    displayLabel?: string;
    options?: CategoricalOption[] | NumberOption;
    type?: OrchestrationDataType;
  }[];
}

export enum InputType {
  LOGICAL_QUERY = 'logical_query',
  NUMBER = 'number',
  NUMBERS = 'numbers',
  BOOLEAN = 'boolean',
  SELECT = 'select',
  MULTISELECT = 'multiselect',
  RADIO = 'radio',
  GROUP = 'group',
}

export enum NewCellColumns {
  THRESHOLD_RESULT_MARKERS = 'threshold_results_marker',
  K_AND_N_NEIGHBOR = 'internally_approved_k_n_neighbor',
}

export enum InputSource {
  STAIN_TYPES_OF_SELECTED_SLIDES = 'stain_types_of_selected_slides',
  AREAS = 'areas',
  INTERMEDIATE_AREAS_PER_TARGET_STAIN = 'intermediate_areas_per_target_stain',
  INTERMEDIATE_DETECTIONS_PER_TARGET_STAIN = 'intermediate_detections_per_target_stain',
  CLASSES_CONFIG_AREAS_PER_TARGET_STAIN = 'classes_config_areas_per_target_stain',
  CELLS = 'cells',
  INTERMEDIATE_CELLS_PER_TARGET_STAIN = 'intermediate_cells_per_target_stain',
  CLASSES_CONFIG_CELLS_PER_TARGET_STAIN = 'classes_config_cells_per_target_stain',
  INTERMEDIATE_CELLS_FEATURES_PER_TARGET_STAIN = 'intermediate_cells_features_per_target_stain',
  CELL_ATTRIBUTES_OF_SELECTED_SLIDES = 'cell_attributes_of_selected_slides',
  CELL_ATTRIBUTES_OF_SELECTED_SLIDES_FOR_REASSIGNMENT = 'cell_attributes_of_selected_slides_for_reassignment',
  AREA_ATTRIBUTES_OF_SELECTED_SLIDES = 'area_attributes_of_selected_slides',
  AREA_ATTRIBUTES_OF_SELECTED_SLIDES_FOR_REASSIGNMENT = 'area_attributes_of_selected_slides_for_reassignment',
  OPTIONS_OF_SELECTED_ATTRIBUTE = 'options_of_selected_attribute',
  TAXONOMY_TAGS = 'taxonomy_tags',
  CELL_GEOMETRY_METADATA = 'cell_geometry_metadata',
  AREA_GEOMETRY_METADATA = 'area_geometry_metadata',
  THRESHOLD_MARKERS = 'threshold_markers',
  INSTANCE_COUNT_THRESHOLDS = 'instance_count_thresholds',
  CELL_BINS = 'cell_bins',
  AREA_BINS = 'area_bins',
}
